import React, { useState, useRef, lazy } from 'react'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import axios from 'axios'
import ReCAPTCHA from 'react-google-recaptcha'
import Loader from 'react-loader-spinner'
import { Fade } from 'react-reveal'

import {
  Outer,
  Inner,
  Content,
  TextWrapper,
  FormWrapper,
  TwoField,
  FullField,
  HalfField,
  Label,
  BottomLeftCircle,
  TopRightCircle,
  TopRightLine1,
  TopRightLine2,
  SubmissionWrapper,
  RadioButtons,
} from './ContactForm.styles'

const Success = lazy(() => import('./primitives/Success'))
const Failure = lazy(() => import('./primitives/Failure'))
import CloseButton from '../Navbar/primitives/CloseButton'
import CtaButton from '../../primitives/CtaButton'

const ContactForm = ({ siteKey, recaptchaKey, referralOptions }) => {
  const [loading, setLoading] = useState(false)
  const [overlayState, setOverlayState] = useState(false)
  const [resStatus, setResStatus] = useState(false)
  const recaptchaRef = useRef(false)
  const isPhone =
    typeof navigator !== 'undefined' &&
    /Android|webOS|iPhone|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    )
  return (
    <Outer>
      <Inner>
        <TopRightCircle />
        <TopRightLine1 />
        <TopRightLine2 />
        <Content>
          <TextWrapper>
            <h1>
              Need to connect?
              <br />
              It's easy.
            </h1>
            <p>
              If you’d like to talk about working together just fill out this
              quick form and we’ll reach out to set up some time to chat.
            </p>
          </TextWrapper>

          <Formik
            initialValues={{
              first: '',
              last: '',
              email: '',
              organization: '',
              referral: '',
              referralOther: '',
              summary: '',
              description: '',
              recaptcha: '',
              botField: '',
              isPhone: isPhone,
            }}
            validationSchema={Yup.object().shape({
              isPhone: Yup.bool(),
              first: Yup.string().max(50, 'Too Long!').required('Required'),
              last: Yup.string().max(50, 'Too Long!').required('Required'),
              email: Yup.string().email('Invalid').required('Required'),
              organization: Yup.string()
                .max(50, 'Too Long!')
                .required('Required'),
              summary: Yup.string().max(150, 'Too Long!').required('Required'),
              description: Yup.string().required('Required'),
              recaptcha: Yup.string().when('isPhone', {
                is: false,
                then: Yup.string().required('Required'),
              }),
              referral: Yup.string().required('Required'),
              botField: Yup.string(),
              referralOther: Yup.string().when('referral', {
                is: 'other',
                then: Yup.string().required(
                  'Please let us know how you heard about us'
                ),
              }),
            })}
            onSubmit={(values, actions) => {
              setLoading(true)
              setResStatus(false)
              setOverlayState(true)
              axios
                .post(
                  `${process.env.GATSBY_CORS_ENDPOINT}/jira`,
                  {
                    fields: {
                      summary: values.summary,
                      customfield_10037: values.first,
                      customfield_10038: values.last,
                      customfield_10039: values.email,
                      customfield_10040: values.organization,
                      customfield_10041:
                        values.referral === 'other'
                          ? values.referralOther
                          : values.referral,
                      project: {
                        key: 'PROMAZO',
                      },
                      issuetype: {
                        name: siteKey,
                      },
                      description: {
                        type: 'doc',
                        version: 1,
                        content: [
                          {
                            type: 'paragraph',
                            content: [
                              {
                                text: values.description,
                                type: 'text',
                              },
                            ],
                          },
                        ],
                      },
                    },
                    recaptcha: {
                      user_token: values.recaptcha,
                    },
                    device: {
                      isPhone: isPhone,
                    },
                    botField: values.botField === '' ? false : true,
                  },
                  {
                    headers: {
                      Accept: '*/*',
                      'Content-Type': 'application/json',
                    },
                  }
                )
                .then((response) => {
                  setLoading(false)
                  setResStatus(!!response.data.id ? !!response.data.id : false)
                  setTimeout(() => setOverlayState(false), 5000)
                  actions.resetForm({})
                  !isPhone &&
                    recaptchaRef.current &&
                    recaptchaRef.current.reset()
                })
                .catch((error) => {
                  console.error(error)
                  !isPhone &&
                    recaptchaRef.current &&
                    recaptchaRef.current.reset()
                  setResStatus(false)
                  setLoading(false)
                })
            }}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <FormWrapper>
                <Form name="contact-form">
                  <TwoField>
                    <HalfField>
                      <Label htmlFor="first">First name</Label>

                      <Field name="first" id="first" placeholder="First Name" />
                      {errors.first && touched.first ? (
                        <div className="error">{errors.first}</div>
                      ) : null}
                    </HalfField>

                    <HalfField>
                      <Label htmlFor="last">Last name</Label>

                      <Field name="last" id="last" placeholder="Last Name" />
                      {errors.last && touched.last ? (
                        <div className="error">{errors.last}</div>
                      ) : null}
                    </HalfField>
                  </TwoField>

                  <TwoField>
                    <HalfField>
                      <Label htmlFor="email">Email</Label>

                      <Field
                        name="email"
                        type="email"
                        id="email"
                        placeholder="Email"
                      />
                      {errors.email && touched.email ? (
                        <div className="error email">{errors.email}</div>
                      ) : null}
                    </HalfField>

                    <HalfField>
                      <Label htmlFor="orgnaization">Organization</Label>

                      <Field
                        name="organization"
                        id="Organization"
                        placeholder="Organization"
                      />
                      {errors.organization && touched.organization ? (
                        <div className="error">{errors.organization}</div>
                      ) : null}
                    </HalfField>
                  </TwoField>

                  <FullField>
                    <Label htmlFor="summary">Subject</Label>

                    <Field
                      name="summary"
                      type="summary"
                      id="summary"
                      placeholder="Subject"
                    />
                    {errors.summary && touched.summary ? (
                      <div className="error">{errors.summary}</div>
                    ) : null}
                  </FullField>

                  <FullField>
                    <Label className="msg" htmlFor="description">
                      Message
                    </Label>
                    <Field
                      rows="8"
                      component="textarea"
                      name="description"
                      id="description"
                      placeholder="Message"
                    />
                    {errors.description && touched.description ? (
                      <div className="alternate-errors">
                        {errors.description}
                      </div>
                    ) : null}
                  </FullField>

                  <RadioButtons>
                    <div id="referral-group">How did you hear about us?</div>
                    <div
                      className="radio-container"
                      role="group"
                      aria-labelledby="referral-group"
                    >
                      {referralOptions.map((option, i) => (
                        <label
                          className="radio-label"
                          key={`referral-option-${i}`}
                        >
                          <Field
                            className="radio-button"
                            type="radio"
                            name="referral"
                            value={option}
                          />
                          {option}
                        </label>
                      ))}

                      <label className="radio-label" htmlFor="other">
                        {errors.referralOther && touched.referralOther ? (
                          <div className="alternate-errors">
                            {errors.referralOther}
                          </div>
                        ) : null}
                        <Field
                          type="radio"
                          className="radio-button"
                          id="other"
                          value="other"
                          name="referral"
                        />
                        Other
                      </label>
                      {values.referral === 'other' && (
                        <>
                          <Label htmlFor="referralOther">
                            Other Referral Specification Field
                          </Label>
                          <Fade>
                            <Field
                              id="referralOther"
                              name="referralOther"
                              placeholder="Please Specify..."
                            />
                          </Fade>
                        </>
                      )}
                    </div>

                    {errors.referral && touched.referral ? (
                      <div className="alternate-errors">{errors.referral}</div>
                    ) : null}
                  </RadioButtons>
                  <Field
                    type="input"
                    name="botField"
                    id="botField"
                    style={{ display: 'none' }}
                  />
                  <div className="submit">
                    {!isPhone && (
                      <div className="recaptcha">
                        <ReCAPTCHA
                          sitekey="6LdmIDIaAAAAAHUdEH_FL-Xn9LiviteWf1TPOE_F"
                          ref={recaptchaRef}
                          onChange={(response) => {
                            setFieldValue('recaptcha', response)
                          }}
                        />
                        {errors.recaptcha && touched.recaptcha && (
                          <div className="error-recaptcha">
                            {errors.recaptcha}
                          </div>
                        )}
                      </div>
                    )}
                    <CtaButton minW="220px" submit>
                      Send Message
                    </CtaButton>
                  </div>
                </Form>
              </FormWrapper>
            )}
          </Formik>
        </Content>
        {overlayState && (
          <SubmissionWrapper onClick={() => !loading && setOverlayState(false)}>
            {loading && (
              <Fade>
                <Loader
                  type="ThreeDots"
                  color="#2BAD60"
                  height="100"
                  width="100"
                />
              </Fade>
            )}
            {!loading && resStatus && (
              <Fade>
                <div className="msg-background">
                  <p>Your message has been sent!</p>
                  <Success />
                </div>
              </Fade>
            )}
            {!loading && !resStatus && (
              <Fade>
                <div className="msg-background">
                  <div className="close">
                    <CloseButton />
                  </div>
                  <p>We were unable to send your message.</p>
                  <CtaButton modalToggle={() => setOverlayState(false)}>
                    Try again
                  </CtaButton>
                  <Failure />
                </div>
              </Fade>
            )}
          </SubmissionWrapper>
        )}
      </Inner>
      <BottomLeftCircle />
    </Outer>
  )
}

ContactForm.defaultProps = {
  referralOptions: [
    'Partnership announcement',
    'An article or interview',
    'Social media',
    'Friends',
    'Colleagues',
  ],
}

export default ContactForm
